<!--
📌 Componente: AnmeldungView.vue
📂 Caminho: src/views/AnmeldungView.vue
📝 Descrição: Tela de login por Mitarbeiter-ID e senha. Define o
  idioma pré-login a partir de localStorage ("flowmax.loginLang").
  Após autenticar, lê usersConfig/{uid}.lang no Firestore, aplica e
  persiste para o próximo acesso.

👁️ Interface:
  - Campos: Mitarbeiter-ID (L+6 dígitos+-DV) e Passwort (aa123456).
  - Toggle mostrar/ocultar senha no input.
  - Erros inline para matrícula, senha e login.

🔗 Dependências:
  - Vue: @/components/AktionButton.vue
  - Utils: @/services/MatrikelService (toDbFormat, calcDV)
  - Firebase: Auth (signInWithEmailAndPassword), Firestore (doc,getDoc)
-->

<template>
  <div
    class="fixed inset-0 bg-cinza-50 flex items-center justify-center overflow-y-auto p-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
  >
    <div
      class="bg-branco p-6 sm:p-8 rounded shadow-lg w-full max-w-sm sm:max-w-md lg:max-w-lg transition-all max-[420px]:bg-transparent max-[420px]:shadow-none"
    >
      <!-- Cabeçalho -->
      <div
        :class="{ 'login-header-shake': avatarState === 'error' }"
        class="mb-8 flex flex-col items-center justify-center gap-4"
      >
        <PraxisAssistentAvatar
          :state="avatarState"
          :id-length="mitarbeiterIdDisplay.length"
        />
        <div class="flex items-center justify-center gap-3 sm:flex-row">
          <img
            src="@/assets/images/ps-logo-128.webp"
            alt="PraxisSecondhand Logo"
            class="h-12 w-12 lg:h-14 lg:w-14"
          />
          <img
            src="@/assets/images/sps-text-128.webp"
            alt="System Praxis Secondhand"
            class="h-6 lg:h-8"
          />
        </div>
      </div>

      <!-- Formulário -->
      <form @submit.prevent="handleLogin" class="space-y-6">
        <!-- Mitarbeiter-ID -->
        <div>
          <label
            for="mitarbeiterId"
            class="flex items-center gap-2 text-sm font-medium text-azul-profundo mb-1"
          >
            <!-- Heroicon: User (Outline) -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            {{ $t("common.employeeId") }}
          </label>
          <input
            id="mitarbeiterId"
            type="text"
            :placeholder="$t('auth.placeholder.employeeId')"
            class="block w-full h-12 p-3 bg-cinza-claro border border-borda-suave rounded focus:ring-2 focus:ring-azul-profundo focus:border-azul-profundo uppercase transition-all outline-none"
            v-model="mitarbeiterIdDisplay"
            @input="onMitarbeiterIdInput"
            @focus="onMitarbeiterIdFocus"
            @blur="resetAvatarState"
            maxlength="9"
            autocomplete="username"
          />
          <p v-if="mitarbeiterIdError" class="mt-1 text-vermelho-suave text-sm">
            {{ mitarbeiterIdError }}
          </p>
        </div>

        <!-- Passwort -->
        <div>
          <label
            for="passwort"
            class="flex items-center gap-2 text-sm font-medium text-azul-profundo mb-1"
          >
            <!-- Heroicon: LockClosed (Outline) -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            {{ $t("auth.password") }}
          </label>
          <div class="relative">
            <input
              id="passwort"
              :type="showPass ? 'text' : 'password'"
              maxlength="8"
              :placeholder="$t('auth.placeholder.password')"
              class="block w-full h-12 p-3 pr-12 bg-cinza-claro border border-borda-suave rounded focus:ring-2 focus:ring-azul-profundo focus:border-azul-profundo lowercase transition-all outline-none"
              v-model="passwort"
              @input="onPasswortInput"
              @focus="onPasswortFocus"
              @blur="resetAvatarState"
              @keyup.enter="handleLogin"
              autocomplete="current-password"
            />
            <button
              type="button"
              @mousedown.prevent
              @click="togglePasswordVisibility"
              class="absolute inset-y-0 right-0 px-3 flex items-center text-cinza-500 hover:text-azul-profundo transition-colors focus:outline-none"
              :title="$t('auth.showPassword')"
            >
              <svg
                v-if="!showPass"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            </button>
          </div>
          <p v-if="passwortError" class="mt-1 text-vermelho-suave text-sm">
            {{ passwortError }}
          </p>
        </div>

        <!-- Botão -->
        <AktionButton
          :label="$t('auth.signin')"
          type="azul"
          class="w-full h-12 text-lg active:scale-95 transition-transform"
          @click="handleLogin"
        />
      </form>

      <!-- Mensagem de erro -->
      <p
        v-if="errorMessage"
        class="mt-6 text-vermelho-suave text-sm text-center font-medium bg-vermelho-fundo p-2 rounded border border-vermelho-fundo"
      >
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
// 🔐 Login + redirecionamento por passwordChangeRequired
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebaseConfig";
import AktionButton from "@/components/AktionButton.vue";
import PraxisAssistentAvatar from "@/components/PraxisAssistentAvatar.vue";
import { toDbFormat, calcDV } from "@/services/MatrikelService";

export default {
  name: "AnmeldungView",
  components: { AktionButton, PraxisAssistentAvatar },
  data() {
    return {
      mitarbeiterIdDisplay: "",
      passwort: "",
      showPass: false,
      mitarbeiterIdError: "",
      passwortError: "",
      errorMessage: "",
      dvValid: false,
      avatarState: "idle",
      focusedInput: "",
      avatarResetTimer: null,
      isLoggingIn: false,
    };
  },
  mounted() {
    try {
      const v = localStorage.getItem("sps.lang");
      const lang = typeof v === "string" && v ? v : "de";
      this.$i18n.locale = lang;
      document.documentElement.lang = lang;
    } catch {
      this.$i18n.locale = "de";
      document.documentElement.lang = "de";
    }
  },
  beforeUnmount() {
    this.clearAvatarReset();
  },
  watch: {
    showPass() {
      if (this.focusedInput === "passwort") {
        this.updatePasswordAvatarState();
      }
    },
  },
  methods: {
    clearAvatarReset() {
      if (this.avatarResetTimer) {
        clearTimeout(this.avatarResetTimer);
        this.avatarResetTimer = null;
      }
    },
    onMitarbeiterIdFocus() {
      this.clearAvatarReset();
      this.focusedInput = "mitarbeiterId";
      this.avatarState = "typing-id";
    },
    onPasswortFocus() {
      this.clearAvatarReset();
      this.focusedInput = "passwort";
      this.updatePasswordAvatarState();
    },
    updatePasswordAvatarState() {
      this.avatarState = this.showPass ? "show-password" : "typing-password";
    },
    togglePasswordVisibility() {
      this.showPass = !this.showPass;
      this.focusedInput = "passwort";
    },
    resetAvatarState() {
      this.focusedInput = "";
      if (this.avatarState !== "loading" && this.avatarState !== "success") {
        this.avatarState = "idle";
      }
    },
    setAvatarError() {
      this.clearAvatarReset();
      this.avatarState = "error";
      this.avatarResetTimer = window.setTimeout(() => {
        this.avatarState = "idle";
        this.avatarResetTimer = null;
      }, 2500);
    },
    setAvatarInputError() {
      this.clearAvatarReset();
      this.avatarState = "error";
    },
    waitForSuccessAvatar() {
      return new Promise((resolve) => window.setTimeout(resolve, 450));
    },
    dateToMillis(v) {
      if (!v) return null;
      if (typeof v === "string") {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d.getTime();
      }
      if (v?.toDate) return v.toDate().getTime();
      if (v instanceof Date) return v.getTime();
      return null;
    },
    hasActiveContract(contracts = []) {
      const today = this.dateToMillis(new Date());
      const sorted = [...contracts].sort((a, b) => {
        const aName = (a.name || "").toString();
        const bName = (b.name || "").toString();
        return bName.localeCompare(aName);
      });
      const current = sorted[0];
      if (!current) return false;
      const endMs = this.dateToMillis(current.endDate);
      if (endMs === null) return true;
      if (today === null) return false;
      return endMs >= today;
    },

    onMitarbeiterIdInput() {
      if (this.focusedInput === "mitarbeiterId") {
        this.clearAvatarReset();
        this.avatarState = "typing-id";
      }
      const raw = String(this.mitarbeiterIdDisplay || "");
      let s = raw.replace(/[^a-zA-Z0-9]/g, "");
      if (s.length > 0) s = s[0].toUpperCase() + s.slice(1);
      s = s.slice(0, 8);
      const letter = s.slice(0, 1).replace(/[^A-Z]/g, "");
      const digits = s.slice(1).replace(/\D/g, "");
      const num6 = digits.slice(0, 6);
      const dv = digits.slice(6, 7);
      let display = letter + num6;
      if (dv.length) display += "-" + dv;
      this.mitarbeiterIdDisplay = display;
      this.dvValid = false;
      this.mitarbeiterIdError = "";
      if (letter && num6.length === 6 && dv.length === 1) {
        const expected = calcDV(letter, num6);
        if (String(expected) !== dv) {
          this.mitarbeiterIdError = this.$t("errors.invalidEmployeeId");
          this.setAvatarInputError();
          return;
        }
        this.dvValid = true;
        this.avatarState = "success";
      }
    },

    onPasswortInput() {
      if (this.focusedInput === "passwort") {
        this.clearAvatarReset();
        this.updatePasswordAvatarState();
      }
      const rawPassword = String(this.passwort || "").toLowerCase();
      let p = rawPassword;
      const a = p.replace(/[^a-z]/g, "").slice(0, 2);
      const b = p.slice(2).replace(/\D/g, "").slice(0, 6);
      this.passwort = (a + b).slice(0, 8);
      const passwordHasInvalidCharacters = rawPassword !== this.passwort;

      if (this.passwort.length > 0 && this.passwort.length < 8) {
        this.passwortError = this.$t("errors.passwordFormat");
      } else if (this.passwort.length === 8) {
        if (!/^[a-z]{2}\d{6}$/.test(this.passwort)) {
          this.passwortError = this.$t("errors.passwordFormat");
        } else {
          this.passwortError = "";
          // 🔑 senha válida e completa → dispara login automático
          this.$nextTick(() => this.handleLogin());
        }
      } else {
        this.passwortError = "";
      }

      if (passwordHasInvalidCharacters) {
        this.setAvatarInputError();
      }
    },

    async handleLogin() {
      if (this.isLoggingIn) return;

      try {
        this.errorMessage = "";

        const m = String(this.mitarbeiterIdDisplay || "").toUpperCase();
        const ok = /^([A-Z])(\d{6})-(\d)$/.test(m);
        if (!ok || !this.dvValid) {
          this.errorMessage = this.$t("errors.invalidEmployeeId");
          this.setAvatarError();
          return;
        }

        const letter = m[0];
        const number6 = m.slice(1, 7);
        const mitarbeiterId = toDbFormat(letter, number6);

        if (!mitarbeiterId) {
          this.errorMessage = this.$t("errors.invalidEmployeeId");
          this.setAvatarError();
          return;
        }

        if (!/^[a-z]{2}\d{6}$/.test(this.passwort)) {
          this.errorMessage = this.$t("errors.passwordFormat");
          this.setAvatarError();
          return;
        }

        const email = `${mitarbeiterId}@praxisgmbh.de`;
        const auth = getAuth();
        this.isLoggingIn = true;
        this.avatarState = "loading";
        await setPersistence(auth, browserLocalPersistence);
        const cred = await signInWithEmailAndPassword(
          auth,
          email,
          this.passwort
        );

        const uid = cred.user.uid;
        sessionStorage.setItem("uid", uid);
        sessionStorage.setItem("mitarbeiterId", mitarbeiterId);
        sessionStorage.setItem("mitarbeiterFull", m);

        const userRef = doc(db, "users", uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data?.passwordChangeRequired === true) {
            this.avatarState = "success";
            await this.waitForSuccessAvatar();
            return this.$router.push({ name: "passwortAendern" });
          }
          const contratos = Array.isArray(data.contracts) ? data.contracts : [];
          const ativo = this.hasActiveContract(contratos);
          if (!ativo) {
            this.errorMessage = this.$t("errors.contractInactive");
            await auth.signOut();
            this.isLoggingIn = false;
            this.setAvatarError();
            return;
          }
        }

        // Pós-login: lê usersConfig/{uid}.lang, aplica e persiste.
        try {
          const cfgRef = doc(db, "usersConfig", uid);
          const cfgSnap = await getDoc(cfgRef);
          const lang = cfgSnap.data()?.lang;
          if (typeof lang === "string" && lang) {
            this.$i18n.locale = lang;
            document.documentElement.lang = lang;
            try {
              localStorage.setItem("sps.lang", lang);
            } catch (err) {
              void err;
            }
          }
        } catch {
          // silencioso
        }

        this.avatarState = "success";
        await this.waitForSuccessAvatar();
        this.$router.push({ name: "startseite" });
      } catch (error) {
        this.isLoggingIn = false;
        const code = error?.code || "";
        this.errorMessage =
          code === "auth/user-not-found"
            ? this.$t("errors.userNotFound")
            : code === "auth/wrong-password"
            ? this.$t("errors.wrongPassword")
            : this.$t("errors.loginFailed");
        this.setAvatarError();
      }
    },
  },
};
</script>

<style scoped>
.login-header-shake {
  animation: login-header-shake 300ms ease-in-out;
}

@keyframes login-header-shake {
  25% {
    transform: translateX(-6px);
  }

  75% {
    transform: translateX(6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-header-shake {
    animation: none;
  }
}
</style>
